import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';
import { getHdFearCenter } from '@/app/human-design/chart/fears/hd-fears.metadata';

/**
 * De drie bewustzijnscentra zoals op de poster, met hun poortvolgorde en de
 * kant waar het driehoek-diagram staat (de beschrijving staat aan de andere
 * kant). `extra: 'general'` hangt het Algemeen-blok onder de Solar-Plexus-
 * beschrijving, net als in het Figma-design.
 */
const HD_AWARENESS_SECTIONS = [
  { key: 'ajna', trianglePlacement: 'right', staircase: 'none' },
  { key: 'spleen', trianglePlacement: 'left', staircase: 'tent' },
  { key: 'solarPlexus', trianglePlacement: 'right', staircase: 'valley', extra: 'general' },
] as const;

/**
 * De kaart-"trap": de kaarten schuiven per rij op zodat ze de driehoek omsluiten
 * (zie het Figma-design). Milt = tent (middelste kaart het verst naar de punt),
 * Solar Plexus = vallei (middelste kaart het verst weg van de punt). Ajna heeft
 * geen trap.
 */
type HdAwarenessStaircase = (typeof HD_AWARENESS_SECTIONS)[number]['staircase'];

type HdAwarenessSectionKey = (typeof HD_AWARENESS_SECTIONS)[number]['key'];

interface AwarenessSectionVm {
  readonly key: HdAwarenessSectionKey;
  readonly title: string;
  readonly description: readonly string[];
  readonly extraTitle: string;
  readonly extraDescription: readonly string[];
  readonly trianglePlacement: 'left' | 'right';
  readonly staircase: HdAwarenessStaircase;
  readonly triangleImage: string;
  readonly triangleAlt: string;
  readonly gates: readonly number[];
}

/**
 * Pagina met de Bewustzijn & angsten cheat sheet, nagebouwd uit het Figma-design
 * "Human Design bewustzijn en angsten" (node 1931-7316, zonder A3-header/branding-
 * footer). Drie secties rond de bewustzijnscentra Ajna, Milt (Spleen) en Solar
 * Plexus: telkens een centrum-beschrijving, een driehoek-diagram en een reeks
 * poort-angstkaarten. Onder de Solar-Plexus-beschrijving staat het Algemeen-blok.
 *
 * Alle teksten komen uit de `human-design/awareness`-scope via
 * {@link HdGateInfoService.cheatSheetTextIn}; de angst-blokjes zelf worden
 * gerenderd door het gedeelde `hd-fear-card`-component. Taalwissel via de gedeelde
 * content-taal-picker; nog niet vertaalde EN-velden vallen terug op NL.
 */
@Component({
  selector: 'hd-awareness-cheat-sheet-page',
  standalone: false,
  templateUrl: './hd-awareness-cheat-sheet-page.component.html',
  styleUrl: './hd-awareness-cheat-sheet-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdAwarenessCheatSheetPageComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly pageTitle = computed(() => this.text('pageTitle'));
  protected readonly pageSubtitle = computed(() => this.text('pageSubtitle'));

  protected readonly sections = computed<readonly AwarenessSectionVm[]>(() =>
    HD_AWARENESS_SECTIONS.map(section => {
      const definition = getHdFearCenter(section.key);
      const title = this.text(`centers.${section.key}.title`);
      const extra = 'extra' in section ? section.extra : undefined;
      return {
        key: section.key,
        title,
        description: this.paragraphs(`centers.${section.key}.description`),
        extraTitle: extra ? this.text(`centers.${extra}.title`) : '',
        extraDescription: extra ? this.paragraphs(`centers.${extra}.description`) : [],
        trianglePlacement: section.trianglePlacement,
        staircase: section.staircase,
        triangleImage: definition.triangleAsset,
        triangleAlt: title,
        gates: definition.gates,
      };
    }),
  );

  constructor() {
    // Beide content-talen eager laden zodat de taalwissel én de NL-fallback
    // meteen correcte tekst opleveren.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  protected print(): void {
    window.print();
  }

  /**
   * Leest een tekst-array uit de scope (Transloco bewaart arrays onder hun
   * dot-key, niet als `key.0`), met NL-fallback voor een (nog) lege EN-array.
   */
  private paragraphs(key: string): readonly string[] {
    const lang = this.lang();
    const value = this.gateInfo.cheatSheetListIn('awareness', key, lang);
    if (value.length > 0 || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetListIn('awareness', key, 'nl');
  }

  /** Scope-tekst in de content-taal, met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string): string {
    const lang = this.lang();
    const value = this.gateInfo.cheatSheetTextIn('awareness', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('awareness', key, 'nl');
  }
}
